import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { format, Locale } from 'date-fns';
import * as _ from 'lodash';
import { SnackBarService } from 'src/app/services/snackbar.service';
import { SnackBarType } from 'src/app/models/Snackbar';
import { PricePeriods } from 'src/app/models/PricePeriod';

interface MonthCover {
    monthAsFormattedString: string,
    cover: number,
    projectedCover: number,
    consumption: number
}

@Component({
    selector: 'app-cover-dialog',
    templateUrl: './cover-dialog.component.html',
    styleUrls: ['cover-dialog.component.scss']
})
export class CoverDialogComponent implements OnInit {

    readonly percentages = [25, 50, 75, 100];

    selectedPercentage: number = 25;
    price: PricePeriods.ElectricityPrices;
    periodLabel: string;
    coverPercentageOverTreshold: boolean = false;
    // Highest % that can still be added without any month going over 100%
    maxAvailablePercentage: number = 100;
    totalConsumption: number = 0;

    coversByYears: { [key: number]: MonthCover[] } = {}

    constructor(
        private readonly dialogRef: MatDialogRef<CoverDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private readonly data: any,
        private _snackbarService: SnackBarService
    ) {
        const locale: Locale | undefined = data.locale;

        this.coversByYears = data.coversByMonth.reduce( (acc: { [key: number]: MonthCover[] }, coverData: any) => {
            const year = coverData.monthAsDate.getFullYear();
            if (!acc[year]) {
                acc[year] = [];
            }

            acc[year].push({
                monthAsFormattedString: format(coverData.monthAsDate, 'MMM', { locale }),
                cover: coverData.cover,
                projectedCover: coverData.cover + this.selectedPercentage,
                consumption: coverData.consumption ?? 0
            });

            return acc;
        }, {});

        const allMonths = Object.values(this.coversByYears).flat();
        this.maxAvailablePercentage = Math.max(0, 100 - (_.maxBy(allMonths, (m) => m.cover)?.cover ?? 0));
        this.totalConsumption = _.sumBy(allMonths, (m) => m.consumption);

        this.price = data.pricePeriod;
        this.periodLabel = data.periodLabel ?? data.pricePeriod.description;

        // Editing an existing cover keeps its value; a new cover starts with the largest option that fits (up to 25%)
        this.selectPercentage(data.pricePeriod.coverPercentage || _.findLast(this.percentages, (p) => p <= Math.min(25, this.maxAvailablePercentage)) || 0);
    }

    ngOnInit(): void {

    }

    // Estimated MWh protected by choosing a given percentage over the whole period
    getProtectedConsumption(percentage: number): number {
        return this.totalConsumption * (percentage / 100);
    }

    // Estimated cost of the protected MWh at this price
    getProtectedCost(percentage: number): number {
        return this.getProtectedConsumption(percentage) * this.price.basePrice;
    }

    selectPercentage(percentage: number) {
        this.selectedPercentage = percentage;
        this.coverPercentageOverTreshold = false

        Object.values(this.coversByYears).forEach((cvmList) => {
            cvmList.forEach((cvm) => {
                cvm.projectedCover = cvm.cover + percentage;

                if (cvm.projectedCover > 100) {
                    this.coverPercentageOverTreshold = true
                }
            });
        });
    }

    closeDialog(apply: boolean) {


        if (apply) {
            if (this.coverPercentageOverTreshold) {
                this._snackbarService.callSnackBar('COVER_PERCENTAGE_OVER_TRESHOLD', SnackBarType.ERROR);
                return;
            }

            this.dialogRef.close(this.selectedPercentage);
        } else {
            this.dialogRef.close(-1);
        }
    }
}
